import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  setDoc
} from '@angular/fire/firestore';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { VideoItem } from '../components/video-carousel/video-carousel';

export interface LocationInfo {
  id: string;
  name: string;
  description: string;
  mapUrl: string;
  gallery: VideoItem[];
  order?: number;
}

export interface FaqItem {
  id?: string;
  question: string;
  answer: string;
  order: number;
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private firestore: Firestore = inject(Firestore);

  // Fallback data for initial development or if Firebase is not yet seeded
  private homeGalleryFallback: VideoItem[] = [
    { id: '1', type: 'youtube', src: 'https://www.youtube.com/embed/eetibzGIt58', thumb: 'https://img.youtube.com/vi/eetibzGIt58/2.jpg', title: 'Windsor Place' },
    { id: '2', type: 'youtube', src: 'https://www.youtube.com/embed/FXhxyWrklJY', thumb: 'https://img.youtube.com/vi/FXhxyWrklJY/2.jpg', title: '5181 Tony St. - 3BD' },
    { id: '3', type: 'youtube', src: 'https://www.youtube.com/embed/y3eaOvbemOI', thumb: 'https://img.youtube.com/vi/y3eaOvbemOI/2.jpg', title: '519 Chris Dr. - 3BD' },
    { id: '4', type: 'youtube', src: 'https://www.youtube.com/embed/YwDnJtiMwoU', thumb: 'https://img.youtube.com/vi/YwDnJtiMwoU/3.jpg', title: '485 Chris Dr. - 3BD' },
    { id: '5', type: 'youtube', src: 'https://www.youtube.com/embed/kvopsMi4CkA', thumb: 'https://img.youtube.com/vi/kvopsMi4CkA/1.jpg', title: '533 Chris Dr. - 4BD' },
    { id: '6', type: 'youtube', src: 'https://www.youtube.com/embed/QjBzYQ6HZOY', thumb: 'https://img.youtube.com/vi/QjBzYQ6HZOY/1.jpg', title: '362 Chris Dr. - 4BD' },
    { id: '7', type: 'image', src: 'img/1.jpg', thumb: 'img/1c.jpg', title: '519 Chris Dr' },
    { id: '8', type: 'image', src: 'img/win1.jpg', thumb: 'img/win1.jpg', title: 'Windsor Place' }
  ];

  getHomeGallery(): Observable<VideoItem[]> {
    const homeRef = doc(this.firestore, 'siteContent/homePage');
    return from(getDoc(homeRef)).pipe(
      map(snap => {
        if (snap.exists()) {
          const data = snap.data();
          return (data['gallery'] as VideoItem[]) || [];
        }
        return this.homeGalleryFallback;
      })
    );
  }

  updateHomeGallery(gallery: VideoItem[]) {
    const homeRef = doc(this.firestore, 'siteContent/homePage');
    return setDoc(homeRef, { gallery }, { merge: true });
  }

  getSettings(): Observable<any> {
    const settingsRef = doc(this.firestore, 'siteContent/settings');
    return from(getDoc(settingsRef)).pipe(
      map(snap => snap.exists() ? snap.data() : {
        phone: '(573) 825-8707',
        youtubeUrl: 'http://www.youtube.com/user/hakerproperties',
        email: 'hakerproperty@gmail.com'
      })
    );
  }

  updateSettings(settings: any) {
    const settingsRef = doc(this.firestore, 'siteContent/settings');
    return setDoc(settingsRef, settings, { merge: true });
  }

  getLocations(): Observable<LocationInfo[]> {
    const locRef = collection(this.firestore, 'locations');
    const q = query(locRef, orderBy('order', 'asc'));
    return collectionData(q, { idField: 'id' }) as Observable<LocationInfo[]>;
  }

  getFaqs(): Observable<FaqItem[]> {
    const faqRef = collection(this.firestore, 'faqs');
    const q = query(faqRef, orderBy('order', 'asc'));
    return collectionData(q, { idField: 'id' }) as Observable<FaqItem[]>;
  }

  // Admin Methods for FAQs
  addFaq(faq: Partial<FaqItem>) {
    const faqRef = collection(this.firestore, 'faqs');
    return addDoc(faqRef, faq);
  }

  updateFaq(id: string, faq: Partial<FaqItem>) {
    const faqDoc = doc(this.firestore, `faqs/${id}`);
    return updateDoc(faqDoc, faq);
  }

  deleteFaq(id: string) {
    const faqDoc = doc(this.firestore, `faqs/${id}`);
    return deleteDoc(faqDoc);
  }

  // Admin Methods for Locations
  addLocation(location: Partial<LocationInfo>) {
    const locRef = collection(this.firestore, 'locations');
    return addDoc(locRef, location);
  }

  updateLocation(id: string, location: Partial<LocationInfo>) {
    const locDoc = doc(this.firestore, `locations/${id}`);
    return updateDoc(locDoc, location);
  }

  deleteLocation(id: string) {
    const locDoc = doc(this.firestore, `locations/${id}`);
    return deleteDoc(locDoc);
  }
}
